import { MenuItem, type MenuItemProps, Tooltip, VisuallyHidden } from '@chakra-ui/react';
import React, { useId } from 'react';

interface GatedMenuItemProps extends MenuItemProps {
  /**
   * When true, the menu item is rendered fully interactive.  When
   * false, the item is shown but disabled and the `disabledReason`
   * tooltip explains why — preserves discoverability of the action
   * for users who don't yet have the right permission, instead of
   * hiding it entirely.
   */
  isAllowed: boolean;
  /**
   * Human-readable explanation rendered as a tooltip on the disabled
   * state.  Required when `isAllowed === false`.  Examples:
   *   - "Only people with manage access can change who can see this"
   *   - "You only have view access to this note"
   */
  disabledReason: string;
}

/**
 * MenuItem that's always rendered but disabled when `isAllowed` is
 * false — with a tooltip explaining the missing capability.
 *
 * Pattern rationale: hiding menu items based on permission keeps the
 * UI tidy but makes the existence of the action invisible.  Users
 * who later gain access have to discover the option from scratch.
 * Showing-but-disabling is the default UX choice elsewhere in Bower
 * (export when nothing to export, etc.) and matches Notion / Linear /
 * Figma — every action is always visible, the user always knows what
 * the system can do, and missing permissions are explained inline.
 *
 * Tooltip on a disabled MenuItem: Chakra MenuItem with `isDisabled`
 * doesn't fire pointer events itself, but Chakra Tooltip's portal
 * wrapper handles this correctly — the tooltip still appears on
 * hover/focus of the disabled item.
 */
export const GatedMenuItem = React.forwardRef<HTMLDivElement, GatedMenuItemProps>(
  ({ isAllowed, disabledReason, children, isDisabled, onClick, ...rest }, ref) => {
    const reasonId = useId();
    const item = (
      <MenuItem
        {...rest}
        ref={ref as React.RefObject<HTMLButtonElement> | null}
        // Both gated props come AFTER the spread and out of `rest`: spread
        // last, a caller's `isDisabled={isSaving}` (false when idle) used to
        // override the gate and re-enable the item for a user without access.
        isDisabled={!isAllowed || isDisabled}
        // Still reachable by the arrow keys when gated, so a keyboard or
        // screen-reader user meets the item and its reason (the tooltip opens
        // on focus, and the reason is its description) instead of skipping it.
        isFocusable={!isAllowed}
        aria-describedby={isAllowed ? undefined : reasonId}
        // Closing the parent menu on click should NOT happen when the
        // item is disabled — Chakra already prevents the onClick, but
        // we belt-and-braces by gating the handler too.
        onClick={isAllowed ? onClick : undefined}
      >
        {children}
      </MenuItem>
    );
    if (isAllowed) return item;
    return (
      <Tooltip label={disabledReason} hasArrow placement="left" openDelay={200}>
        {/* Wrap in a span so the tooltip target receives pointer
            events even when the MenuItem itself is disabled. */}
        <span>
          {item}
          <VisuallyHidden id={reasonId}>{disabledReason}</VisuallyHidden>
        </span>
      </Tooltip>
    );
  },
);

GatedMenuItem.displayName = 'GatedMenuItem';
